import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar-rac";
import { cn } from "@/lib/utils";
import { getLocalTimeZone, today, CalendarDate } from "@internationalized/date";
import type { DateValue } from "react-aria-components";
import { useState } from "react";

interface DateFilterProps {
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
}

export const DateFilter = ({ date, setDate }: DateFilterProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleDateSelect = (value: DateValue | null) => {
    if (!value) {
      setDate(undefined);
    } else {
    const localDate = value.toDate(getLocalTimeZone());
    setDate(localDate);
    }
    setIsPopoverOpen(false);
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Any Date';
    return format(date, 'PPP');
  };

  const dateToCalendarDate = (date: Date | undefined) => {
    if (!date) return null;
    return new CalendarDate(
      date.getFullYear(),
      date.getMonth() + 1,
      date.getDate()
    );
  };

  return (
    <div className="space-y-1.5">
      <label className="text-[15px] text-brand-secondary font-normal">
        Delivery Date
      </label>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            onClick={() => setIsPopoverOpen(true)}
            className={cn(
              "w-full justify-start text-left font-normal bg-white border-2 h-[42px] text-[13px] rounded-lg transition-colors",
              date 
                ? "bg-[#967F81]/30 border-[#967F81] text-[#2C1810]" 
                : "border-gray-300 hover:border-[#843329] text-[#2C1810]"
            )}
          >
            <CalendarIcon className="mr-2 h-3.5 w-3.5 text-[#2C1810]" />
            {formatDate(date)}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white border border-brand-primary/20 shadow-lg rounded-md" align="start">
          <div className="flex flex-col">
            <button
              onClick={() => handleDateSelect(null)}
              className="w-full px-4 py-2 text-[13px] font-normal text-brand-secondary hover:bg-[brand-primary]/10 border-b border-brand-primary/20"
            >
              Any Date
            </button>
            <div className="p-2">
              <Calendar
                value={dateToCalendarDate(date)}
                onChange={handleDateSelect}
                minValue={today(getLocalTimeZone())}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};